"use client";

// ============================================================
// CLUBHUB – Register „Mitglieder“ einer Gruppe
// ============================================================
// Jede Zeile ist EIN Zeitraum (`GroupMembership`), nicht eine
// Person: wer 2009 bis 2016 und wieder seit 2021 dabei ist, steht
// unter „Alle“ zweimal — und unter „Aktuell“ einmal, mit dem Hinweis
// auf den frueheren Zeitraum. Beendet wird mit einem Datum; die
// Historie bleibt (ClubDesk ueberschreibt sie).
//
// Mit Funktion zuerst, in der Rangfolge der Aemter — der Vorstand
// liest sich so wie im Vereinsregister.
// ============================================================
import { useMemo, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import {
  CameraOff,
  Ellipsis,
  GlobeLock,
  History,
  IdCard,
  Pencil,
  UserMinus,
  UserPlus,
  Undo2,
  UsersRound,
} from "lucide-react";
import {
  Avatar,
  Button,
  DialogAmRumpf,
  DropdownItem,
  DropdownMenu,
  DropdownSeparator,
  EmptyState,
  TableBody,
  TableCell,
  TableHeader,
  TableHead,
  TableRow,
  buttonVariants,
  toast,
} from "@/components/ui";
import {
  Filterkopf,
  Filterloesen,
  Filterzurueck,
  Fusstext,
  Listenblatt,
  Listenfuss,
  Listenkoerper,
  Listentabelle,
  Luecke,
  Schiene,
  SortableHead,
  SortableHeaderRow,
  Suchfeld,
  UEBER_DER_ZEILE,
  useAnsicht,
  Zaehlknopf,
  Zeilenverweis,
} from "@/components/liste";
import { ContactStatusBadge } from "@/components/mitglieder/status-badge";
import { istFehler } from "@/lib/action-result";
import {
  MITGLIEDER_DEFAULT_SORT,
  MITGLIEDER_PARAMS,
  MITGLIEDER_SORT,
  ZEITRAEUME,
  mitgliederFilterZahl,
  type MitgliederAnsicht,
} from "@/lib/gruppen/ansicht";
import { aktuelleZeitraeume, alterAm, funktionsRang, mitgliederzahl, stand, type ZugehoerigkeitsStand } from "@/lib/gruppen/logik";
import { plusTage } from "@/lib/gruppen/datum";
import type { PersonKurzView, ZugehoerigkeitView } from "@/lib/gruppen/views";
import { sortRows } from "@/lib/sorting";
import { cn, formatDate } from "@/lib/utils";
import { useGruppenBestand } from "./gruppen-bestand";
import { MitgliederDialog } from "./mitglieder-dialog";
import { BeendenDialog, FunktionDialog } from "./zugehoerigkeit-dialoge";
import { StandEtikett } from "./zeichen";

type Zeile = {
  z: ZugehoerigkeitView;
  p: PersonKurzView | undefined;
  s: ZugehoerigkeitsStand;
  /** Andere, beendete Zeitraeume derselben Person in dieser Gruppe. */
  frueher: ZugehoerigkeitView[];
  /** Steht unter „Aktuell“ (laufend, oder kuenftiger Zugang ohne laufenden Zeitraum). */
  imAktuell: boolean;
  /** Ein spaeterer Zeitraum derselben Person, der an diesen anschliesst (Funktionswechsel). */
  danach: ZugehoerigkeitView | null;
};

type Dimension = "zeitraum" | "funktion" | "foto";

const jahr = (iso: string | null) => (iso ? iso.slice(0, 4) : "?");

export function GruppenMitglieder({ id, ansicht }: { id: string; ansicht: MitgliederAnsicht }) {
  const b = useGruppenBestand();
  const { setzen, isPending } = useAnsicht();
  const router = useRouter();
  const [funktionFuer, setFunktionFuer] = useState<ZugehoerigkeitView | null>(null);
  const [beendenFuer, setBeendenFuer] = useState<ZugehoerigkeitView | null>(null);
  const [aufnehmen, setAufnehmen] = useState<string[] | null>(null);
  const gruppe = b.gruppen.find((g) => g.id === id);
  const { heute, personen, zugehoerigkeiten } = b;

  const alle: Zeile[] = useMemo(() => {
    const eigene = zugehoerigkeiten.filter((z) => z.groupId === id);
    const imAktuell = new Set(aktuelleZeitraeume(eigene, heute).map((z) => z.id));
    return eigene.map((z) => ({
      z,
      p: personen[z.contactId],
      s: stand(z, heute),
      frueher: eigene.filter((x) => x.contactId === z.contactId && x.id !== z.id && stand(x, heute) === "beendet"),
      imAktuell: imAktuell.has(z.id),
      danach:
        stand(z, heute) === "aktuell" && z.until
          ? (eigene.find((x) => x.contactId === z.contactId && x.id !== z.id && x.since === plusTage(z.until!, 1)) ?? null)
          : null,
    }));
  }, [zugehoerigkeiten, personen, id, heute]);

  if (!gruppe) return null;

  const nadel = ansicht.q.toLowerCase();
  const passt = (r: Zeile, ausser?: Dimension) => {
    if (ausser !== "zeitraum") {
      if (ansicht.zeitraum === "aktuell" && !r.imAktuell) return false;
      if (ansicht.zeitraum === "frueher" && r.s !== "beendet") return false;
    }
    if (ausser !== "funktion" && ansicht.nurFunktion && !r.z.function) return false;
    if (ausser !== "foto" && ansicht.ohneFoto && !(r.p && !r.p.isOrganization && !r.p.consentPhotos)) return false;
    if (nadel) {
      const heu = [r.p?.displayName, r.p?.memberNumber, r.z.function].filter(Boolean).join(" ").toLowerCase();
      if (!heu.includes(nadel)) return false;
    }
    return true;
  };

  const gezeigt = sortRows(
    alle.filter((r) => passt(r)),
    ansicht.sort,
    {
      funktion: {
        compare: (a, c) =>
          funktionsRang(a.z.function) - funktionsRang(c.z.function) ||
          (a.p?.displayName ?? "").localeCompare(c.p?.displayName ?? "", "de"),
      },
      name: (r) => r.p?.displayName ?? "",
      seit: (r) => r.z.since,
      bis: (r) => r.z.until,
    },
    (a, c) => (c.z.since ?? "").localeCompare(a.z.since ?? ""),
  );

  const fuerZeitraum = alle.filter((r) => passt(r, "zeitraum"));
  const zeitraumZaehler = {
    aktuell: fuerZeitraum.filter((r) => r.imAktuell).length,
    frueher: fuerZeitraum.filter((r) => r.s === "beendet").length,
    alle: fuerZeitraum.length,
  };
  const funktionZaehler = alle.filter((r) => passt(r, "funktion") && r.z.function).length;
  const fotoZaehler = alle.filter((r) => passt(r, "foto") && r.p && !r.p.isOrganization && !r.p.consentPhotos).length;
  const filterZahl = mitgliederFilterZahl(ansicht);
  const personenZahl = new Set(gezeigt.map((r) => r.z.contactId)).size;
  const mitFunktion = gezeigt.filter((r) => r.z.function).length;
  const ohneFoto = gezeigt.filter((r) => r.p && !r.p.isOrganization && !r.p.consentPhotos).length;
  const schreiben = b.rechte.schreiben && !gruppe.isArchived;
  const zahl = mitgliederzahl(b.gruppen, zugehoerigkeiten, id, heute);

  function zeileMenue(r: Zeile) {
    const neuerer = alle.some((x) => x.z.contactId === r.z.contactId && x.z.id !== r.z.id && (x.z.since ?? "") > (r.z.since ?? ""));
    const ausgetreten = r.p?.status === "FORMER" || r.p?.status === "DECEASED";
    return (
      <DropdownMenu
        trigger={
          <button
            type="button"
            aria-label={`Aktionen für ${r.p?.displayName ?? "Person"}`}
            className="text-text-tertiary hover:bg-bg-tertiary hover:text-text-primary duration-fast grid h-8 w-8 place-items-center rounded-lg transition-colors"
          >
            <Ellipsis className="h-4 w-4" aria-hidden />
          </button>
        }
      >
        {schreiben && r.s !== "beendet" && (
          <>
            <DropdownItem onClick={() => setFunktionFuer(r.z)}>
              <Pencil className="h-4 w-4" aria-hidden />
              {r.z.function ? "Funktion ändern" : "Funktion eintragen"}
            </DropdownItem>
            <DropdownItem onClick={() => setBeendenFuer(r.z)}>
              <UserMinus className="h-4 w-4" aria-hidden />
              {r.z.until ? "Ende ändern" : "Zugehörigkeit beenden"}
            </DropdownItem>
          </>
        )}
        {schreiben && r.z.until && !neuerer && (
          <DropdownItem
            onClick={async () => {
              const e = await b.endeZuruecknehmen(r.z.id);
              if (istFehler(e)) toast.error(e.error);
              else toast.success(`Ende zurückgenommen — ${r.p?.displayName ?? "die Person"} ist wieder ohne Enddatum dabei`);
            }}
          >
            <Undo2 className="h-4 w-4" aria-hidden />
            Ende zurücknehmen
          </DropdownItem>
        )}
        {schreiben && r.s === "beendet" && !neuerer && !ausgetreten && (
          <DropdownItem onClick={() => setAufnehmen([r.z.contactId])}>
            <UserPlus className="h-4 w-4" aria-hidden />
            Erneut aufnehmen
          </DropdownItem>
        )}
        {schreiben && <DropdownSeparator />}
        <DropdownItem onClick={() => router.push(`/mitglieder/${r.z.contactId}`)}>
          <IdCard className="h-4 w-4" aria-hidden />
          Zum Mitglied
        </DropdownItem>
      </DropdownMenu>
    );
  }

  const datenschutz = (p: PersonKurzView | undefined, klein = false) => {
    if (!p || p.isOrganization) return <span className="text-text-tertiary">–</span>;
    if (p.consentPhotos && p.consentInternet) return <span className="text-caption text-text-tertiary">{klein ? "" : "Alles erlaubt"}</span>;
    return (
      <span className="text-warning flex items-center gap-1.5">
        {!p.consentPhotos && (
          <span title="Keine Foto-Einwilligung — nicht auf Mannschaftsfotos und in Spielberichten" className="inline-flex items-center gap-1">
            <CameraOff className="h-3.5 w-3.5" aria-hidden />
            {!klein && <span className="text-caption">Keine Fotos</span>}
            <span className="sr-only">Keine Foto-Einwilligung</span>
          </span>
        )}
        {!p.consentInternet && (
          <span title="Keine Einwilligung Internet und Social Media — Name nicht auf Website und Kanälen" className="inline-flex items-center gap-1">
            <GlobeLock className="h-3.5 w-3.5" aria-hidden />
            {!klein && <span className="text-caption">Nicht online</span>}
            <span className="sr-only">Keine Einwilligung Internet und Social Media</span>
          </span>
        )}
      </span>
    );
  };

  const unterzeile = (r: Zeile) => {
    const a = r.p && !r.p.isOrganization ? alterAm(r.p.birthDate, heute) : null;
    const teile = [r.p?.memberNumber ? `Nr. ${r.p.memberNumber}` : null, a !== null ? `${a} Jahre` : null];
    if (r.danach) teile.push(`ab ${formatDate(r.danach.since)}: ${r.danach.function ?? "ohne Funktion"}`);
    if (r.frueher.length > 0 && r.s !== "beendet") teile.push(`zuvor ${r.frueher.map((f) => `${jahr(f.since)}–${jahr(f.until)}`).join(", ")}`);
    return teile.filter(Boolean).join(" · ");
  };

  const leer =
    filterZahl > 0 ? (
      <EmptyState
        icon={UsersRound}
        title={ansicht.zeitraum === "frueher" && !nadel ? "Noch keine Historie" : "Niemand passt zu diesem Filter"}
        description={ansicht.zeitraum === "frueher" ? "Wer die Gruppe verlässt, erscheint hier — mit Zeitraum und Funktion." : "Ändere die Suche oder setze die Filter zurück."}
        action={<Filterloesen params={MITGLIEDER_PARAMS} />}
      />
    ) : zahl.gesamt > zahl.direkt ? (
      <EmptyState
        icon={UsersRound}
        title="Direkt ist niemand in dieser Gruppe"
        description={`Die ${zahl.gesamt} Personen stehen in den Untergruppen. Direkt gehört hierher, wer für alle zuständig ist — die Abteilungsleitung zum Beispiel.`}
        action={
          <Link href={`/gruppen/${id}/untergruppen`} className={buttonVariants({ variant: "outline" })}>
            Zu den Untergruppen
          </Link>
        }
      />
    ) : (
      <EmptyState
        icon={UsersRound}
        title="Noch niemand in dieser Gruppe"
        description="Nimm die ersten Mitglieder auf — mehrere auf einmal, mit gemeinsamem Beginn."
        action={
          schreiben ? (
            <Button onClick={() => setAufnehmen([])}>
              <UserPlus className="h-4 w-4" aria-hidden />
              Mitglieder hinzufügen
            </Button>
          ) : undefined
        }
      />
    );

  return (
    <Listenblatt>
      <Filterkopf
        isPending={isPending}
        oben={
          <>
            <Suchfeld wert={ansicht.q} platzhalter="Name, Nummer, Funktion …" beschriftung="Mitglieder der Gruppe durchsuchen" />
            <Luecke />
            <Schiene>
              {ZEITRAEUME.map((t) => (
                <Zaehlknopf
                  key={t.schluessel}
                  label={t.label}
                  anzahl={zeitraumZaehler[t.schluessel]}
                  aktiv={ansicht.zeitraum === t.schluessel}
                  titel={t.hinweis}
                  onClick={() => setzen({ zeitraum: t.schluessel === "aktuell" ? null : t.schluessel })}
                />
              ))}
            </Schiene>
          </>
        }
        unten={
          <>
            <Schiene>
              <Zaehlknopf
                label="Mit Funktion"
                anzahl={funktionZaehler}
                aktiv={ansicht.nurFunktion}
                titel="Ämter in der Gruppe — Trainerin, Kassenwart, Kapitän"
                onClick={() => setzen({ funktion: ansicht.nurFunktion ? null : "1" })}
              />
              <Zaehlknopf
                label="Ohne Foto-Einwilligung"
                anzahl={fotoZaehler}
                aktiv={ansicht.ohneFoto}
                titel="Nicht auf Mannschaftsfotos, in Spielberichten und auf der Website zeigen"
                onClick={() => setzen({ foto: ansicht.ohneFoto ? null : "ohne" })}
              />
            </Schiene>
            <Filterzurueck anzahl={filterZahl} params={MITGLIEDER_PARAMS} />
          </>
        }
      />

      {gezeigt.length === 0 ? (
        <Listenkoerper className="p-5">{leer}</Listenkoerper>
      ) : (
        <>
          <Listentabelle viewportClassName="max-md:hidden">
            <TableHeader>
              <SortableHeaderRow sort={ansicht.sort} defaultColumn={MITGLIEDER_DEFAULT_SORT}>
                <SortableHead column="name" {...MITGLIEDER_SORT.name}>NAME</SortableHead>
                <SortableHead column="funktion" {...MITGLIEDER_SORT.funktion}>FUNKTION</SortableHead>
                <SortableHead column="seit" {...MITGLIEDER_SORT.seit}>SEIT</SortableHead>
                <SortableHead column="bis" {...MITGLIEDER_SORT.bis}>BIS</SortableHead>
                <TableHead>STAND</TableHead>
                <TableHead>DATENSCHUTZ</TableHead>
                <TableHead className="w-10" aria-label="Aktionen" />
              </SortableHeaderRow>
            </TableHeader>
            <TableBody>
              {gezeigt.map((r) => (
                <TableRow key={r.z.id} className={cn("border-border-subtle border-b", r.s === "beendet" && "text-text-secondary")}>
                  <TableCell className="w-full max-w-0">
                    <div className="flex min-w-0 items-center gap-3">
                      <Avatar firstName={r.p?.firstName ?? "?"} lastName={r.p?.lastName ?? ""} size="sm" inRow hint={false} />
                      <div className="min-w-0">
                        <Zeilenverweis href={`/mitglieder/${r.z.contactId}`} className="block truncate">
                          {r.p?.displayName ?? "Unbekannt"}
                        </Zeilenverweis>
                        <p className="text-caption text-text-tertiary flex items-center gap-1 truncate">
                          {r.frueher.length > 0 && r.s !== "beendet" && <History className="h-3 w-3 shrink-0" aria-hidden />}
                          <span className="truncate">{unterzeile(r)}</span>
                        </p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {r.z.function ?? <span className="text-text-tertiary">–</span>}
                  </TableCell>
                  <TableCell className="tabular-nums whitespace-nowrap">{formatDate(r.z.since) || <span className="text-text-tertiary">–</span>}</TableCell>
                  <TableCell className="tabular-nums whitespace-nowrap">{formatDate(r.z.until) || <span className="text-text-tertiary">–</span>}</TableCell>
                  <TableCell>
                    <span className="flex items-center gap-1.5">
                      <StandEtikett z={r.z} heute={heute} />
                      {r.p && r.p.status !== "ACTIVE" && <ContactStatusBadge status={r.p.status} />}
                    </span>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">{datenschutz(r.p)}</TableCell>
                  <TableCell className="w-10 pr-2">
                    <div className={UEBER_DER_ZEILE}>{zeileMenue(r)}</div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Listentabelle>

          <Listenkoerper className="p-3 md:hidden">
            <ul className="bg-bg-primary border-border divide-border-subtle divide-y overflow-hidden rounded-xl border">
              {gezeigt.map((r) => (
                <li key={r.z.id} className={cn("relative flex items-start gap-3 px-3 py-3", r.s === "beendet" && "opacity-75")}>
                  <Avatar firstName={r.p?.firstName ?? "?"} lastName={r.p?.lastName ?? ""} size="md" hint={false} />
                  <div className="min-w-0 flex-1">
                    <Link href={`/mitglieder/${r.z.contactId}`} className="text-body-sm text-text-primary block truncate font-medium after:absolute after:inset-0 after:content-['']">
                      {r.p?.displayName ?? "Unbekannt"}
                    </Link>
                    {r.z.function && <p className="text-caption text-text-secondary truncate">{r.z.function}</p>}
                    <p className="text-caption text-text-tertiary tabular-nums">
                      {r.z.since ? `seit ${formatDate(r.z.since)}` : "ohne Beginn"}
                      {r.z.until ? ` · bis ${formatDate(r.z.until)}` : ""}
                    </p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                      <StandEtikett z={r.z} heute={heute} />
                      {r.p && r.p.status !== "ACTIVE" && <ContactStatusBadge status={r.p.status} />}
                      {datenschutz(r.p, true)}
                    </div>
                  </div>
                  <div className={cn(UEBER_DER_ZEILE, "shrink-0")}>{zeileMenue(r)}</div>
                </li>
              ))}
            </ul>
          </Listenkoerper>
        </>
      )}

      <Listenfuss>
        <Fusstext titel="Summe" zusatz={`${personenZahl} ${personenZahl === 1 ? "Person" : "Personen"}${gezeigt.length !== personenZahl ? ` in ${gezeigt.length} Zeiträumen` : ""}`} />
        <span className="text-body-sm text-text-secondary">
          Mit Funktion <span className="numeric text-text-primary font-medium">{mitFunktion}</span>
        </span>
        {ohneFoto > 0 && (
          <span className="text-body-sm text-text-secondary">
            Ohne Foto-Einwilligung <span className="numeric text-warning font-medium">{ohneFoto}</span>
          </span>
        )}
      </Listenfuss>

      {/* Am Rumpf: die Zeilen bilden eigene Stapel (`UEBER_DER_ZEILE`). */}
      <DialogAmRumpf>
        <FunktionDialog z={funktionFuer} gruppe={gruppe} onClose={() => setFunktionFuer(null)} />
        <BeendenDialog z={beendenFuer} gruppe={gruppe} onClose={() => setBeendenFuer(null)} />
        <MitgliederDialog offen={aufnehmen !== null} onClose={() => setAufnehmen(null)} gruppe={gruppe} vorgabe={aufnehmen ?? []} />
      </DialogAmRumpf>
    </Listenblatt>
  );
}
